import "./consensusActivity.css"
import React, { useState, useEffect, useRef } from 'react';
import styled from "styled-components";
import { Fullscreen } from "components/shared/Fullscreen/Fullscreen";
import { VideosBar } from "components/pages/Session/shared/VideosBar/VideosBar";
import { ConsensusActivityRender } from "./ConsensusActivityRender";
import { ConsensusLeftPanel } from "./ConsensusLeftPanel";
import { ConsensusTransitionView } from "./ConsensusTransitionView";
import { PostSoloTimeBanner } from "./PostSoloTimeBanner";
import { DiscussionAnalyzed } from "components/pages/Session/DiscussionAnalyzed/DiscussionAnalyzed";
import { colors, constants } from "utils";
import { Timer } from "./Timer/Timer";
import { useConsensusActivity } from "hooks/useConsensusActivity/useConsensusActivity";
import { useMembers } from "hooks/livekit/useMembers/useMembers";
import { useLocalParticipantUserId } from "hooks/useLocalParticipant/useLocalParticipant";

export const ConsensusActivity = () => {
  const {
    timerValue,
    hideQuestion,
    disableOptions,
    selectedIndex,
    isShowRequestMoreTimeButton,
    isMoreTimeRequested,
    isChallengeSolved,
    currentTransitionState,
    transitionViewText,
    transitionViewSubtext,
    surveyData,
    isShowTransitionView,
    announcePhase,
    explanation,
    keyTerms,
    keyTermsDefinition,
    challenge,
    onORAnswer,
    onMCAnswer,
    mcSubmissionError,
    dispatchMoreTimeRequest,
    dispatchSpeakPromptRequest,
    onSurveyResponse,
    dismissChallengeSurvey,
    showStartNewSessionButton,
    isSpokenPromptUsed,
    setMcSubmissionError,
    discussionAnalysis,
    isAnalysisLoading,
    showDiscussionAnalyzed,
    session,
    studentResponses,
    previousRoundResponses,
    collaborationTip,
    questionNumber,
    teamRound,
    talkMoves,
    prepareToShare,
    currentSpeechPhrase,
    isSpeechPlaying,
    postSoloBannerActive,
    dismissPostSoloBanner,
  } = useConsensusActivity();

  const { members } = useMembers();
  const currentUserId = useLocalParticipantUserId();
  const [showAssistantText, setShowAssistantText] = useState(false);

  const getParticipantName = (userId) => {
    if (!userId) return "Student";
    const member = members.find(m => userId.includes(m.userId));

    return member?.participant?.name || "Student";
  };

  useEffect(() => {
    if (discussionAnalysis && !isAnalysisLoading) {
      setShowAssistantText(true);
    }
  }, [discussionAnalysis, isAnalysisLoading]);

  // The window scroll left over from answering the previous screen would
  // otherwise clip the top of the next question or phase. Instant, so the
  // root's smooth scroll-behavior doesn't animate the jump on landing.
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [challenge?.id, currentTransitionState]);

  const TimerComponent = <Timer
    timerValue={timerValue}
    selectedIndex={selectedIndex}
    isMoreTimeRequested={isMoreTimeRequested}
    isShowRequestMoreTimeButton={isShowRequestMoreTimeButton}
    currentTransitionState={currentTransitionState}
    dispatchMoreTimeRequest={dispatchMoreTimeRequest}
  />

  return (
    <StyledFullscreen>
      <Frame>
        <LeftColumn>
          <ConsensusLeftPanel
            mode={currentTransitionState}
            questionNumber={questionNumber}
            teamRound={teamRound}
            talkMoves={talkMoves}
            timer={TimerComponent}
            timerValue={timerValue}
            explanation={explanation}
            collaborationTip={collaborationTip}
            prepareToShare={prepareToShare}
            speechPhrase={currentSpeechPhrase}
            isSpeechPlaying={isSpeechPlaying}
          />
        </LeftColumn>
        {challenge && <MainColumn>
          <ConsensusActivityRender
            sessionLayout
            showPromptFormula
            hideQuestion={hideQuestion}
            selectedIndex={selectedIndex}
            challenge={challenge}
            disableOptions={disableOptions}
            onMCAnswer={onMCAnswer}
            onORAnswer={onORAnswer}
            isMathJaxSet={true}
            isShowTransitionView={isShowTransitionView}
            keyTerms={keyTerms}
            keyTermsDefinition={keyTermsDefinition}
            dispatchSpeakPromptRequest={dispatchSpeakPromptRequest}
            isSpokenPromptUsed={isSpokenPromptUsed}
            mcSubmissionError={mcSubmissionError}
            setMcSubmissionError={setMcSubmissionError}
            currentTransitionState={currentTransitionState}
            sessionId={session?.id}
            studentResponses={studentResponses}
            previousRoundResponses={previousRoundResponses}
            currentUserId={currentUserId}
          />
        </MainColumn>}
      </Frame>
      <ConsensusTransitionView
        currentTransitionState={currentTransitionState}
        show={isShowTransitionView}
        announcePhase={announcePhase}
        text={transitionViewText}
        subtext={transitionViewSubtext}
        isCelebrating={isChallengeSolved}
        surveyData={surveyData}
        onSurveyResponse={onSurveyResponse}
        dismissChallengeSurvey={dismissChallengeSurvey}
        showStartNewSessionButton={showStartNewSessionButton}
      />
      {postSoloBannerActive && (
        <PostSoloTimeBanner
          onSubmit={(value) => onSurveyResponse("challenge_survey", value)}
          onDismiss={dismissPostSoloBanner}
        />
      )}
      <DiscussionAnalyzed
        display={showDiscussionAnalyzed}
        isLoading={isAnalysisLoading}
        level={discussionAnalysis?.level || 1}
        quote={discussionAnalysis?.quote || ""}
        name={getParticipantName(discussionAnalysis?.name)}
        badgeTitle={discussionAnalysis?.badgeTitle || ""}
        badgeType={discussionAnalysis?.badgeType || "Collaboration"}
        phrase={currentSpeechPhrase || ""}
        showText={showAssistantText}
        setShowText={setShowAssistantText}
        isPlayingAudio={isSpeechPlaying}
      />
      <div style={{ display: "none" }}>
        <VideosBar />
      </div>
    </StyledFullscreen>
  );
};

export const StyledFullscreen = styled(Fullscreen)`
  background: ${colors.milkGlass500};
`;

// Sized to the VISIBLE viewport: plain 100vh includes the space behind mobile
// browser chrome, which made the whole session screen scrollable by that
// difference on iPad. dvh tracks the real visible height where supported.
const Frame = styled.div`
  background: ${colors.milkGlass500};
  display: flex;
  min-height: 100vh;
  min-height: 100dvh;
`;

// The session's three-column frame. Desktop keeps the historical 405px left
// reserve; at tablet width the columns take the Figma spec's 24px margins and
// gutters: 24 + 360 (left panel) + 24 on the left, 24 + 48 (control rail) + 24
// on the right.
const LeftColumn = styled.div`
  background: ${colors.milkGlass500};
  position: fixed;
  width: 405px;
  height: 100%;
  /* Fixed positioning traps descendants' z-index in this column's stacking
     context; without a raised order here the rubric flyout paints under the
     question column's answer rows. The columns don't overlap, so this only
     affects overlays that intentionally cross into the middle. */
  z-index: 2;

  @media ${constants.TABLET_MEDIA_QUERY} {
    width: 360px;
  }
`;

const MainColumn = styled.div`
  display: flex;
  flex-grow: 1;
  justify-content: center;
  margin-left: 405px;
  /* Always reserve the control rail's full footprint (24 gutter + 48 rail +
     24 margin) — a thinner margin let content run under the rail icons at
     widths just above the tablet breakpoint. */
  margin-right: 96px;

  @media ${constants.TABLET_MEDIA_QUERY} {
    margin-left: 408px;
  }
`;
